import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { formatSgd, formatNum, apiErr } from "../../lib/utils";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Calculator, Info, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const FREQ = [
  { v: "monthly", l: "Monthly" }, { v: "quarterly", l: "Quarterly" },
  { v: "semi_annual", l: "Semi-annual" }, { v: "annual", l: "Annual" },
];
const ROI_METHODS = [
  { v: "simple_roi", l: "Simple ROI" }, { v: "payback", l: "Payback" },
  { v: "npv", l: "NPV" }, { v: "irr", l: "IRR" }, { v: "abatement_cost", l: "Abatement cost" },
];

const Field = ({ label, value, onChange, suffix, testid, type = "number", hint }) => (
  <div>
    <label className="text-[10px] text-muted-foreground">{label}</label>
    <div className="flex items-center gap-1">
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        aria-invalid={hint?.level === "error"}
        className={`h-8 ${hint?.level === "error" ? "border-red-500 focus-visible:ring-red-500/40" : hint?.level === "warning" ? "border-amber-500 focus-visible:ring-amber-500/40" : ""}`} />
      {suffix && <span className="text-[10px] text-muted-foreground shrink-0">{suffix}</span>}
    </div>
    {hint && (
      <div className={`mt-0.5 text-[10px] flex items-center gap-1 ${hint.level === "error" ? "text-red-500" : "text-amber-500"}`}
        data-testid={testid ? `${testid}-hint` : undefined}>
        <AlertTriangle className="h-2.5 w-2.5 shrink-0" /> {hint.msg}
      </div>
    )}
  </div>
);

const _err = (msg) => ({ level: "error", msg });
const _warn = (msg) => ({ level: "warning", msg });

// Inline validation for the ROI/match inputs — catches bad data before it reaches calculations.
function validateRoi(f) {
  const n = (v) => (v === "" || v == null ? null : Number(v));
  const isNum = (v) => v != null && !Number.isNaN(v);
  const h = {};
  const inv = n(f.investment_required), fund = n(f.funding_required), sav = n(f.expected_annual_savings),
    ctax = n(f.expected_carbon_tax_avoided), carb = n(f.expected_carbon_reduction),
    energy = n(f.expected_energy_savings), water = n(f.expected_water_savings),
    life = n(f.useful_life_years), rate = n(f.interest_rate), tenure = n(f.tenure_months),
    disc = n(f.discount_rate);
  const neg = (v) => isNum(v) && v < 0;

  if (neg(inv)) h.investment_required = _err("Cannot be negative");
  if (neg(fund)) h.funding_required = _err("Cannot be negative");
  else if (isNum(fund) && isNum(inv) && inv > 0 && fund > inv) h.funding_required = _warn("Funding exceeds total investment");
  [["expected_annual_savings", sav], ["expected_carbon_tax_avoided", ctax], ["expected_carbon_reduction", carb],
   ["expected_energy_savings", energy], ["expected_water_savings", water]].forEach(([k, v]) => { if (neg(v)) h[k] = _err("Cannot be negative"); });

  if (isNum(life) && life <= 0) h.useful_life_years = _err("Must be greater than 0");
  else if (isNum(life) && life > 50) h.useful_life_years = _warn("Unusually long (>50 yrs)");

  if (neg(rate)) h.interest_rate = _err("Cannot be negative");
  else if (isNum(rate) && rate > 30) h.interest_rate = _warn("Unusually high (>30%)");

  if (neg(tenure)) h.tenure_months = _err("Cannot be negative");
  else if (isNum(tenure) && tenure > 0 && tenure % 1 !== 0) h.tenure_months = _err("Use whole months");
  else if (isNum(tenure) && isNum(life) && life > 0 && tenure / 12 > life) h.tenure_months = _warn("Tenure exceeds useful life of the asset");
  else if (isNum(tenure) && tenure > 600) h.tenure_months = _warn("Unusually long (>50 yrs)");

  if (neg(disc)) h.discount_rate = _err("Cannot be negative");
  else if (isNum(disc) && disc > 25) h.discount_rate = _warn("Unusually high (>25%)");
  return h;
}

const Metric = ({ label, value, tone, testid }) => (
  <div className="card-flat p-3" data-testid={testid}>
    <div className="label-eyebrow text-[10px]">{label}</div>
    <div className={`font-mono text-lg font-bold ${tone || ""}`}>{value}</div>
  </div>
);

export default function BudgetRoiTab({ i, onChanged }) {
  const imp = i.impact || {};
  const ri = i.roi_inputs || {};
  const [f, setF] = useState({
    investment_required: i.investment_required ?? "",
    funding_required: i.funding_required ?? "",
    expected_annual_savings: i.expected_annual_savings ?? imp.cost_savings_sgd ?? "",
    expected_carbon_tax_avoided: i.expected_carbon_tax_avoided ?? "",
    expected_carbon_reduction: i.expected_carbon_reduction ?? imp.emissions_t ?? "",
    expected_energy_savings: i.expected_energy_savings ?? imp.energy_mwh ?? "",
    expected_water_savings: i.expected_water_savings ?? imp.water_m3 ?? "",
    useful_life_years: i.useful_life_years ?? 10,
    interest_rate: ri.interest_rate ?? "",
    tenure_months: ri.tenure_months ?? "",
    repayment_frequency: ri.repayment_frequency || "monthly",
    discount_rate: ri.discount_rate ?? 5,
    roi_method: i.roi_method || "simple_roi",
  });
  const [roi, setRoi] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get(`/initiatives/${i.id}/roi`).then((r) => setRoi(r.data.roi)).catch(() => {}); }, [i.id]);

  const num = (v) => (v === "" || v == null ? undefined : Number(v));
  const hints = validateRoi(f);
  const errorFields = Object.keys(hints).filter((k) => hints[k].level === "error");
  const hasErrors = errorFields.length > 0;
  const warnCount = Object.values(hints).filter((x) => x.level === "warning").length;

  const calc = async () => {
    if (hasErrors) return toast.error("Fix the highlighted fields before calculating");
    if (!num(f.investment_required)) return toast.error("Enter the investment required first");
    setBusy(true);
    try {
      const body = {
        investment_required: num(f.investment_required), funding_required: num(f.funding_required),
        expected_annual_savings: num(f.expected_annual_savings), expected_carbon_tax_avoided: num(f.expected_carbon_tax_avoided),
        expected_carbon_reduction: num(f.expected_carbon_reduction), expected_energy_savings: num(f.expected_energy_savings),
        expected_water_savings: num(f.expected_water_savings), useful_life_years: num(f.useful_life_years),
        interest_rate: num(f.interest_rate), tenure_months: num(f.tenure_months),
        repayment_frequency: f.repayment_frequency, discount_rate: num(f.discount_rate),
        roi_method: f.roi_method, save: true,
      };
      const r = await api.post(`/initiatives/${i.id}/roi`, body);
      setRoi(r.data.roi);
      toast.success("ROI calculated & saved");
      onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="init-budget-roi-tab">
      <div className="rounded-sm border border-amber-500/30 bg-amber-500/5 p-2.5 text-[11px] text-amber-600/90 flex gap-2">
        <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
        ROI calculations are estimates based on your assumptions and platform data. Validate financial assumptions with your finance team and bank representative.
      </div>

      {/* Inputs */}
      <div className="card-flat p-4 space-y-3">
        <div className="label-eyebrow">Investment & benefit assumptions</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Field label="Investment required (S$)" value={f.investment_required} onChange={(v) => setF({ ...f, investment_required: v })} testid="roi-investment" hint={hints.investment_required} />
          <Field label="Funding required (S$)" value={f.funding_required} onChange={(v) => setF({ ...f, funding_required: v })} testid="roi-funding" hint={hints.funding_required} />
          <Field label="Expected annual savings (S$)" value={f.expected_annual_savings} onChange={(v) => setF({ ...f, expected_annual_savings: v })} testid="roi-savings" hint={hints.expected_annual_savings} />
          <Field label="Carbon tax avoided (S$/yr)" value={f.expected_carbon_tax_avoided} onChange={(v) => setF({ ...f, expected_carbon_tax_avoided: v })} testid="roi-carbontax" hint={hints.expected_carbon_tax_avoided} />
          <Field label="Emissions reduction (tCO₂e/yr)" value={f.expected_carbon_reduction} onChange={(v) => setF({ ...f, expected_carbon_reduction: v })} testid="roi-carbon" hint={hints.expected_carbon_reduction} />
          <Field label="Energy savings (MWh/yr)" value={f.expected_energy_savings} onChange={(v) => setF({ ...f, expected_energy_savings: v })} hint={hints.expected_energy_savings} />
          <Field label="Water savings (m³/yr)" value={f.expected_water_savings} onChange={(v) => setF({ ...f, expected_water_savings: v })} hint={hints.expected_water_savings} />
          <Field label="Useful life (years)" value={f.useful_life_years} onChange={(v) => setF({ ...f, useful_life_years: v })} hint={hints.useful_life_years} />
        </div>
        <div className="label-eyebrow pt-1">Financing assumptions</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Field label="Interest rate (% p.a.)" value={f.interest_rate} onChange={(v) => setF({ ...f, interest_rate: v })} testid="roi-rate" hint={hints.interest_rate} />
          <Field label="Tenure (months)" value={f.tenure_months} onChange={(v) => setF({ ...f, tenure_months: v })} testid="roi-tenure" hint={hints.tenure_months} />
          <div>
            <label className="text-[10px] text-muted-foreground">Repayment frequency</label>
            <Select value={f.repayment_frequency} onValueChange={(v) => setF({ ...f, repayment_frequency: v })}>
              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>{FREQ.map((x) => <SelectItem key={x.v} value={x.v}>{x.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Field label="Discount rate (%)" value={f.discount_rate} onChange={(v) => setF({ ...f, discount_rate: v })} hint={hints.discount_rate} />
          <div>
            <label className="text-[10px] text-muted-foreground">ROI method</label>
            <Select value={f.roi_method} onValueChange={(v) => setF({ ...f, roi_method: v })}>
              <SelectTrigger className="h-8" data-testid="roi-method"><SelectValue /></SelectTrigger>
              <SelectContent>{ROI_METHODS.map((x) => <SelectItem key={x.v} value={x.v}>{x.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[11px]" data-testid="roi-validation-summary">
            {hasErrors ? <span className="text-red-500 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {errorFields.length} field(s) need fixing before calculating</span>
              : warnCount > 0 ? <span className="text-amber-500 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {warnCount} warning(s) — review before relying on results</span>
              : <span className="text-muted-foreground">Inputs look valid.</span>}
          </div>
          <Button variant="primary" size="sm" onClick={calc} disabled={busy || hasErrors} data-testid="roi-calc-btn">
            <Calculator className="h-4 w-4" /> {busy ? "Calculating…" : "Calculate & save"}
          </Button>
        </div>
      </div>

      {roi && !roi.needs_investment && (
        <>
          {/* Investment summary */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <Metric label="Investment" value={formatSgd(roi.investment_required)} testid="roi-out-investment" />
            <Metric label="Internal budget" value={formatSgd(roi.internal_budget)} />
            <Metric label="Funding required" value={roi.funding_required != null ? formatSgd(roi.funding_required) : "—"} />
            <Metric label="Funding gap" value={formatSgd(roi.funding_gap)} tone={roi.funding_gap > 0 ? "text-[#FFB800]" : ""} />
            <Metric label="Useful life" value={`${roi.useful_life_years} yrs`} />
          </div>
          {/* ROI outputs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Metric label="Payback (pre-financing)" value={roi.payback_years_before_financing != null ? `${roi.payback_years_before_financing} yrs` : "—"} testid="roi-out-payback" />
            <Metric label="Payback (after financing)" value={roi.payback_years_after_financing != null ? `${roi.payback_years_after_financing} yrs` : "—"} />
            <Metric label="ROI (after financing)" value={roi.roi_pct_after_financing != null ? `${roi.roi_pct_after_financing}%` : "—"} tone="text-[#00E65B]" testid="roi-out-roi" />
            <Metric label="NPV" value={roi.npv != null ? formatSgd(roi.npv) : "—"} />
            <Metric label="Annual net benefit" value={formatSgd(roi.annual_net_benefit)} />
            <Metric label="Gross annual benefit" value={formatSgd(roi.gross_annual_benefit)} />
            <Metric label="Lifetime CO₂" value={`${formatNum(roi.lifetime_co2_tonnes)} t`} />
            <Metric label="Cost per tCO₂e" value={roi.cost_per_tco2e != null ? formatSgd(roi.cost_per_tco2e) : "—"} testid="roi-out-cost-tco2e" />
          </div>

          {!roi.rate_available && (
            <div className="rounded-sm border border-border bg-surface-hover/40 p-2.5 text-[11px] text-muted-foreground flex gap-2" data-testid="roi-rate-note">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-[#FFB800]" /> {roi.rate_note}
            </div>
          )}

          {/* Financing cost simulator */}
          {roi.financing_simulator?.length > 0 && (
            <div className="card-flat p-4">
              <div className="label-eyebrow mb-2">Financing cost simulator</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="roi-fin-simulator">
                  <thead><tr className="text-muted-foreground text-left">
                    <th className="py-1">Scenario</th><th className="text-right">Interest</th><th className="text-right">Annual savings</th>
                    <th className="text-right">Annual financing cost</th><th className="text-right">Net after financing</th><th className="text-right">Payback</th>
                  </tr></thead>
                  <tbody>
                    {roi.financing_simulator.map((s) => (
                      <tr key={s.name} className="border-t border-border">
                        <td className="py-1.5 capitalize">{s.name}</td>
                        <td className="text-right font-mono">{s.interest_rate}%</td>
                        <td className="text-right font-mono">{formatSgd(s.annual_savings)}</td>
                        <td className="text-right font-mono">{formatSgd(s.annual_financing_cost)}</td>
                        <td className="text-right font-mono">{formatSgd(s.net_benefit_after_financing)}</td>
                        <td className="text-right font-mono">{s.payback_years != null ? `${s.payback_years} yrs` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Sensitivity */}
          {roi.sensitivity?.length > 0 && (
            <div className="card-flat p-4">
              <div className="label-eyebrow mb-2">ROI sensitivity</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="roi-sensitivity">
                  <thead><tr className="text-muted-foreground text-left">
                    <th className="py-1">Scenario</th><th className="text-right">Interest rate</th><th className="text-right">Annual savings</th>
                    <th className="text-right">Payback</th><th className="text-right">NPV</th>
                  </tr></thead>
                  <tbody>
                    {roi.sensitivity.map((s) => (
                      <tr key={s.scenario} className="border-t border-border">
                        <td className="py-1.5">{s.scenario}</td>
                        <td className="text-right font-mono">{s.interest_rate}%</td>
                        <td className="text-right font-mono">{formatSgd(s.annual_savings)}</td>
                        <td className="text-right font-mono">{s.payback_years != null ? `${s.payback_years} yrs` : "—"}</td>
                        <td className="text-right font-mono">{s.npv != null ? formatSgd(s.npv) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
      {roi?.needs_investment && <div className="text-sm text-muted-foreground">{roi.message}</div>}
    </div>
  );
}
